import type React from 'react';

import { useGetUser, useGetRole } from '@vlabs/api/artemy/client';
import type { UserId } from '@vlabs/api/artemy/model';
import { usePageAccess, withPermissions } from '@vlabs/permissions';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { CopyButton } from '@vlabs/uikit';
import { Link } from '@vlabs/uikit/components/controls/link/Link';
import { routes } from '@vlapps/point/src/app/routes';
import { generatePath } from 'react-router-dom';

import { ResetUserPasswordButton } from '@vlabs/monokit/features';
import { EditUserButton } from '@vlabs/monokit/features/point/edit-user';

import { useUserDetailsGeneralInfoTranslation } from './i18n';
import st from './UserDetailsGeneralInfo.module.sass';
import { valueOrHyphen } from '../../../shared/lib/utils';

const EditUserWithAccess = withPermissions('users', 'update')(EditUserButton);

interface UserDetailsGeneralInfoProps {
  userId: UserId;
}

function UserDetailsGeneralInfo({
  userId,
}: UserDetailsGeneralInfoProps): React.JSX.Element {
  const { t } = useUserDetailsGeneralInfoTranslation();

  const { data: user } = useGetUser(userId);
  const { data: role } = useGetRole(user?.role_id ?? '', { query: { enabled: !!user?.role_id } });
  const canViewRoleDetails = usePageAccess('roles_details');

  const fullName = valueOrHyphen(user?.full_name);
  const email = valueOrHyphen(user?.email);
  const userRole = valueOrHyphen(role?.name);
  const createdTime = valueOrHyphen(parseISOToLocale(user?.create_time));
  const updateTime = valueOrHyphen(parseISOToLocale(user?.update_time));
  const id = valueOrHyphen(user?.user_id);
  const roleId = user?.role_id;

  const renderRole = (): React.JSX.Element => {
    if (canViewRoleDetails && roleId) {
      return (
        <Link
          className={st.Link}
          kind="secondary"
          to={generatePath(routes.roles.view, { roleId })}
        >
          {userRole}
        </Link>
      );
    }

    return <span className={st.Data}>{userRole}</span>;
  };

  return (
    <div className={st.GeneralInfo}>
      <div className={st.Header}>
        <h6 className={st.Title}>{t('title')}</h6>
        <EditUserWithAccess userId={userId} />
      </div>

      <div className={st.UserInfo}>
        <div
          className={st.Item}
          data-testid="userInfoItem.email"
        >
          <span className={st.Label}>{t('email')}</span>
          <span className={st.Email}>{email}</span>
        </div>

        <div className={st.Separator} />

        <div
          className={st.Item}
          data-testid="userInfoItem.password"
        >
          <span className={st.Label}>{t('password')}</span>
          <div className={st.Password}>
            <span className={st.Data}>********</span>
            <ResetUserPasswordButton
              className={st.ResetButton}
              size="small"
              userId={user?.user_id}
            />
          </div>
        </div>

        <div className={st.Separator} />

        <div
          className={st.Item}
          data-testid="userInfoItem.full_name"
        >
          <span className={st.Label}>{t('full_name')}</span>
          <span className={st.Data}>{fullName}</span>
        </div>

        <div className={st.Separator} />

        <div
          className={st.Item}
          data-testid="userInfoItem.role"
        >
          <span className={st.Label}>{t('role')}</span>
          {renderRole()}
        </div>

        <div className={st.Separator} />

        <div
          className={st.Item}
          data-testid="userInfoItem.create_time"
        >
          <span className={st.Label}>{t('create_time')}</span>
          <span className={st.Data}>{createdTime}</span>
        </div>

        <div className={st.Separator} />

        <div
          className={st.Item}
          data-testid="userInfoItem.update_time"
        >
          <span className={st.Label}>{t('update_time')}</span>
          <span className={st.Data}>{updateTime}</span>
        </div>

        <div className={st.Separator} />

        <div
          className={st.Item}
          data-testid="userInfoItem.user_id"
        >
          <span className={st.Label}>{t('user_id')}</span>
          <div className={st.UserId}>
            <span className={st.Data}>{id}</span>
            <CopyButton
              className={st.CopyButton}
              value={id}
            />
          </div>
        </div>

        <div className={st.Separator} />
      </div>
    </div>
  );
}

export { UserDetailsGeneralInfo };
